import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter/dist/cjs';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkEmoji from 'remark-emoji';
import remarkGfm from 'remark-gfm';
import { Pluggable } from 'unified';

export function CustomReactMarkdown({
  content,
  className,
  wrapperClassName,
}: {
  content: string;
  className?: string;
  wrapperClassName?: string;
}) {
  return (
    <div
      translate="no"
      className={` ${
        wrapperClassName || 'tw-px-4 tw-py-3 tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-flex'
      }`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm as Pluggable, [remarkEmoji, { emoticon: false }] as Pluggable, remarkBreaks]}
        rehypePlugins={[rehypeRaw as unknown as Pluggable]} // enable raw HTML
        components={markdownComponents}
        className={`${className || 'tw-text-gray-600'}`}
      >
        {content?.replace(/\n/gi, '&nbsp; \n')}
      </ReactMarkdown>
    </div>
  );
}

const markdownComponents = {
  code({ inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <SyntaxHighlighter
        style={dracula}
        language={match[1]}
        PreTag="div"
        className="tw-rounded-lg tw-overflow-x-auto tw-p-2"
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className="tw-bg-gray-100 tw-text-black tw-rounded tw-p-1 tw-font-mono tw-text-sm">{children}</code>
    );
  },

  pre({ children }: any) {
    return <pre className="tw-overflow-x-auto tw-my-4">{children}</pre>;
  },

  // Headings
  h1: ({ children }: any) => <h1 className="tw-text-3xl tw-font-bold tw-mb-3">{children}</h1>,
  h2: ({ children }: any) => <h2 className="tw-text-2xl tw-font-bold tw-mb-2">{children}</h2>,
  h3: ({ children }: any) => <h3 className="tw-text-xl tw-font-bold tw-mb-1">{children}</h3>,
  h4: ({ children }: any) => <h4 className="tw-text-lg tw-font-bold tw-mb-1">{children}</h4>,
  h5: ({ children }: any) => <h5 className="tw-text-sm ">{children}</h5>,
  h6: ({ children }: any) => <h6 className="tw-text-xs ">{children}</h6>,

  // Paragraphs and text formatting
  p: ({ children }: any) => {
    return <p className="tw-leading-relaxed">{children}</p>;
  },

  strong: ({ children }: any) => <strong className="tw-font-bold">{children}</strong>,

  em: ({ children }: any) => <em className="tw-italic">{children}</em>,

  // Lists
  ul: ({ children }: any) => <ul className="tw-list-disc tw-pl-4 tw-mb-2">{children}</ul>,
  ol: ({ children }: any) => <ol className="tw-list-decimal tw-pl-4 tw-mb-2">{children}</ol>,
  li: ({ children }: any) => <li className="tw-mb-1 tw-pl-2">{children}</li>,

  // Blockquotes
  blockquote: ({ children }: any) => (
    <blockquote className="tw-border-l-4 tw-border-gray-300 tw-pl-4 tw-italic tw-my-4">{children}</blockquote>
  ),

  // Links
  a: ({ href, children }: any) => (
    <a href={href} className="tw-hover:text-blue-800 tw-underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),

  // Tables
  table: ({ children }: any) => (
    <div className="tw-overflow-x-auto tw-my-4">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">{children}</table>
    </div>
  ),
  thead: ({ children }: any) => <thead className="tw-bg-gray-50">{children}</thead>,
  tbody: ({ children }: any) => <tbody className="tw-divide-y tw-divide-gray-200">{children}</tbody>,
  tr: ({ children }: any) => <tr>{children}</tr>,

  td: ({ children }: any) => <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">{children}</td>,
  th: ({ children }: any) => (
    <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
      {children}
    </th>
  ),

  // Horizontal rule
  hr: () => <hr className="tw-my-4 tw-border-t tw-border-gray-300" />,

  // Images
  img: ({ src, alt }: any) => <img src={src} alt={alt} className="tw-max-w-full tw-h-auto tw-rounded-lg tw-my-4" />,
};
